import { useState, type ReactNode } from "react";

/**
 * A password-style text input with a show/hide "eye" toggle pinned to its right edge. Used for
 * secrets the user is TYPING (an SSO client secret, a new password / confirm). The reveal shows the
 * clear text only while toggled on; it defaults to hidden. Callers pass the usual input props; the
 * component owns only the reveal state.
 *
 * NOTE (issue 5): never use this to reveal a value the server did not return. For an existing SSO
 * secret the field is blank until the admin types a replacement, so there is nothing to leak.
 */
export function EyeIcon({ off }: { off: boolean }): ReactNode {
  // `off=true` → the "hidden/slashed" eye (currently showing clear text, click to hide).
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
      <circle cx="12" cy="12" r="3" />
      {off && <line x1="3" y1="3" x2="21" y2="21" />}
    </svg>
  );
}

export function RevealInput(props: {
  readonly id: string;
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly className?: string;
  readonly placeholder?: string;
  readonly autoComplete?: string;
  readonly disabled?: boolean;
  readonly ariaLabel?: string;
}): ReactNode {
  const [show, setShow] = useState(false);
  return (
    <span className="oa-account__input-reveal">
      <input
        id={props.id}
        className={props.className ?? "oa-account__input"}
        type={show ? "text" : "password"}
        value={props.value}
        disabled={props.disabled === true}
        {...(props.placeholder !== undefined ? { placeholder: props.placeholder } : {})}
        {...(props.autoComplete !== undefined ? { autoComplete: props.autoComplete } : {})}
        {...(props.ariaLabel !== undefined ? { "aria-label": props.ariaLabel } : {})}
        onChange={(e) => props.onChange(e.target.value)}
      />
      {props.value !== "" && (
        <button
          type="button"
          className="oa-account__reveal-btn"
          aria-label={show ? "Hide" : "Show"}
          aria-pressed={show}
          title={show ? "Hide" : "Show"}
          onClick={() => setShow((v) => !v)}
        >
          <EyeIcon off={show} />
        </button>
      )}
    </span>
  );
}
